import PropTypes from "prop-types";

import { Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import {
  formatNumber,
  getShortReference,
  getTransactionPath,
} from "@/features/shared";

/**
 * Component for displaying a single upstream transaction reference as a clickable link.
 * Used for showing parent/source transactions (e.g., a GR showing its source PO).
 * Displays format: "ABC123 (5)" where 5 is the quantity.
 *
 * @param {string} referenceNumber - The transaction reference number (required)
 * @param {number} quantity - The quantity associated with the transaction (optional)
 * @param {boolean} shortReference - Whether to display shortened reference numbers (default: true)
 * @param {string} orderType - For Purchase Orders, specify "STANDARD" or "LAB" to route correctly (optional)
 * @param {boolean} clickable - Whether the link should be clickable (default: true)
 * @returns Rendered link or "-" if no referenceNumber
 *
 * @example
 * // Display PO reference with quantity and orderType
 * <UpstreamTransactionLink
 *   referenceNumber="PO-20251007-ABC123"
 *   quantity={5}
 *   orderType="STANDARD"
 *   clickable={hasPermission(PermissionEnum.READ_PURCHASE_ORDERS)}
 * />
 * // Output: ABC123 (5) - routes to /procurement/purchase-orders/standard/PO-20251007-ABC123
 *
 * @example
 * // Display Lab PO reference
 * <UpstreamTransactionLink
 *   referenceNumber="PO-20251007-ABC123"
 *   orderType="LAB"
 * />
 * // Output: ABC123 - routes to /procurement/purchase-orders/lab/PO-20251007-ABC123
 *
 * @example
 * // Display full reference with quantity
 * <UpstreamTransactionLink
 *   referenceNumber="PO-20251007-ABC123"
 *   quantity={10}
 *   shortReference={false}
 * />
 * // Output: PO-20251007-ABC123 (10)
 */
export const UpstreamTransactionLink = ({
  referenceNumber,
  quantity,
  shortReference = true,
  orderType = null,
  clickable = true,
}) => {
  if (!referenceNumber) return "-";

  const path = getTransactionPath(referenceNumber, orderType);
  if (!path) return "-";

  const displayText = shortReference
    ? getShortReference(referenceNumber)
    : referenceNumber;

  const linkText = quantity
    ? `${displayText} (${formatNumber(quantity)})`
    : displayText;

  // If not clickable, render as plain text
  if (!clickable) {
    return (
      <Typography variant="body2" component="span">
        {linkText}
      </Typography>
    );
  }

  return (
    <Link
      component={RouterLink}
      to={path}
      underline="hover"
      sx={{ fontWeight: 500 }}
    >
      {linkText}
    </Link>
  );
};

UpstreamTransactionLink.propTypes = {
  referenceNumber: PropTypes.string,
  quantity: PropTypes.number,
  shortReference: PropTypes.bool,
  orderType: PropTypes.string,
  clickable: PropTypes.bool,
};
